import { gql } from '@apollo/client';

/**
 * Keystone's lender surface, as the lenders screen reads and writes it.
 *
 * Every read here names both statuses, which is the staff view and needs
 * `lenders` Read; the screen is only drawn for a caller that has it. Keystone
 * has no `lender(id:)`, so the detail page asks `lenders(keys:)` for the one
 * lender its URL names — keys are immutable and never reused, which is what
 * makes them safe to put in a URL.
 */

export type LenderStatus = 'ACTIVE' | 'INACTIVE';
export type LenderDocumentStatus = 'ACTIVE' | 'ARCHIVED';
export type LenderDocumentPurpose = 'CLIENT_DOCUMENT';

export const ALL_LENDER_STATUSES: readonly LenderStatus[] = ['ACTIVE', 'INACTIVE'];
export const ALL_DOCUMENT_STATUSES: readonly LenderDocumentStatus[] = ['ACTIVE', 'ARCHIVED'];
export const CLIENT_DOCUMENT: LenderDocumentPurpose = 'CLIENT_DOCUMENT';

export interface LenderFile {
  id: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
}

export interface LenderDocumentVersion {
  id: string;
  versionNo: number | null;
  title: string | null;
  fileId: string | null;
  createdAt: string | null;
  createdById: string | null;
  file: LenderFile | null;
}

export interface LenderDocument {
  id: string;
  key: string | null;
  position: number | null;
  status: LenderDocumentStatus | null;
  currentVersionId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  currentVersion: LenderDocumentVersion | null;
  versions: LenderDocumentVersion[];
}

export interface LenderDocumentSummary {
  id: string;
  status: LenderDocumentStatus | null;
}

export interface LenderSummary {
  id: string;
  key: string | null;
  name: string | null;
  country: string | null;
  status: LenderStatus | null;
  documents: LenderDocumentSummary[];
}

export interface Lender extends Omit<LenderSummary, 'documents'> {
  createdAt: string | null;
  updatedAt: string | null;
  documents: LenderDocument[];
}

const VERSION_FIELDS = `
  id
  versionNo
  title
  fileId
  createdAt
  createdById
  file {
    id
    filename
    contentType
    sizeBytes
  }
`;

export const LENDERS_QUERY = gql`
  query Lenders($statuses: [LenderStatus!]) {
    lenders(statuses: $statuses) {
      id
      key
      name
      country
      status
      documents(purposes: [CLIENT_DOCUMENT], statuses: [ACTIVE, ARCHIVED]) {
        id
        status
      }
    }
  }
`;

export interface LendersQueryResult {
  lenders: LenderSummary[];
}

export const LENDER_DETAIL_QUERY = gql`
  query LenderDetail($key: String!) {
    lenders(keys: [$key], statuses: [ACTIVE, INACTIVE]) {
      id
      key
      name
      country
      status
      createdAt
      updatedAt
      documents(purposes: [CLIENT_DOCUMENT], statuses: [ACTIVE, ARCHIVED]) {
        id
        key
        position
        status
        currentVersionId
        createdAt
        updatedAt
        currentVersion {
          ${VERSION_FIELDS}
        }
        versions {
          ${VERSION_FIELDS}
        }
      }
    }
  }
`;

export interface LenderDetailResult {
  lenders: Lender[];
}

/** One signed URL, minted when someone asks to download rather than for every
 *  row on the page: each is a V4 signature and an IAM round trip. */
export const FILE_DOWNLOAD_QUERY = gql`
  query LenderFileDownload($id: UUID!, $filename: String) {
    file(id: $id) {
      id
      originalDownloadUrl(disposition: ATTACHMENT, filename: $filename)
    }
  }
`;

export interface FileDownloadResult {
  file: { id: string; originalDownloadUrl: string | null } | null;
}

const LENDER_FIELDS = `
  id
  key
  name
  country
  status
`;

export const CREATE_LENDER = gql`
  mutation CreateLender($key: String!, $name: String!, $country: String!) {
    createLender(key: $key, name: $name, country: $country) {
      ${LENDER_FIELDS}
    }
  }
`;

export const UPDATE_LENDER = gql`
  mutation UpdateLender($id: UUID!, $name: String, $country: String) {
    updateLender(id: $id, name: $name, country: $country) {
      ${LENDER_FIELDS}
    }
  }
`;

export const SET_LENDER_STATUS = gql`
  mutation SetLenderStatus($id: UUID!, $status: LenderStatus!) {
    setLenderStatus(id: $id, status: $status) {
      ${LENDER_FIELDS}
    }
  }
`;

export const DELETE_LENDER = gql`
  mutation DeleteLender($id: UUID!) {
    deleteLender(id: $id)
  }
`;

const DOCUMENT_FIELDS = `
  id
  position
  status
  currentVersionId
`;

export const CREATE_LENDER_DOCUMENT = gql`
  mutation CreateLenderDocument(
    $lenderId: UUID!
    $key: String!
    $fileId: UUID!
    $title: String!
  ) {
    createLenderDocument(
      lenderId: $lenderId
      purpose: CLIENT_DOCUMENT
      key: $key
      fileId: $fileId
      title: $title
    ) {
      ${DOCUMENT_FIELDS}
    }
  }
`;

export const PUBLISH_LENDER_DOCUMENT_VERSION = gql`
  mutation PublishLenderDocumentVersion($documentId: UUID!, $fileId: UUID, $title: String) {
    publishLenderDocumentVersion(documentId: $documentId, fileId: $fileId, title: $title) {
      ${DOCUMENT_FIELDS}
    }
  }
`;

export const SET_LENDER_DOCUMENT_STATUS = gql`
  mutation SetLenderDocumentStatus($id: UUID!, $status: LenderDocumentStatus!) {
    setLenderDocumentStatus(id: $id, status: $status) {
      ${DOCUMENT_FIELDS}
    }
  }
`;

export const REORDER_LENDER_DOCUMENTS = gql`
  mutation ReorderLenderDocuments($lenderId: UUID!, $orderedIds: [UUID!]!) {
    reorderLenderDocuments(lenderId: $lenderId, purpose: CLIENT_DOCUMENT, orderedIds: $orderedIds) {
      ${DOCUMENT_FIELDS}
    }
  }
`;

export const DELETE_LENDER_DOCUMENT = gql`
  mutation DeleteLenderDocument($id: UUID!) {
    deleteLenderDocument(id: $id)
  }
`;

export const REVERT_LENDER_DOCUMENT = gql`
  mutation RevertLenderDocument($documentId: UUID!, $versionNo: Int!) {
    revertLenderDocument(documentId: $documentId, versionNo: $versionNo) {
      ${DOCUMENT_FIELDS}
    }
  }
`;
